<!--
	This component renders the New Credit card form for payments.
	One can access the encrypted data via getEncryptedCardInfo().
	To verify all data is valid in the form, simply call isValid().
	Props:
	- can_save_card - optional boolean default=false indicating if the user can choose to save this credit card for future use (if the transaction is successful).
	- public_key_url - required string the source of the paytrace public key.  At this scope, we can't access env variables and therefore we must be provided the url for loading our encryption key.
-->
<template>
	<div class="paytrace-payment">
		<form class="container-fluid" autocomplete="off" @submit="onSubmit($event)">
			<div class="row p-b-10">
				<div class="col-xs-12">
					<label for="ccNumber">Credit Card Number</label>
					<input type="number" class="form-control pt-encrypt" id="ccNumber" name="ccNumber" placeholder="•••• •••• •••• ••••" v-model="card_number">
				</div>
			</div>

			<div class="row p-b-10">
				<div class="col-xs-12 col-sm-6">
					<label for="ccCSC">Security Code</label>
					<input type="number" class="form-control pt-encrypt" id="ccCSC" name="ccCSC" placeholder="•••" v-model="cvv">
				</div>

				<div class="col-xs-12 col-sm-6">
					<label for="postal_cd">Zip Code</label>
					<input type="text" class="form-control" id="postal_cd" name="postal_cd" v-model="postal_cd" />
				</div>
			</div>

			<div class="row p-b-10">
				<div class="col-xs-12">
					<label>Expiration Date</label>
					<div class="row">
						<div class="col-xs-6">
							<select v-model="exp_month" class="form-control">
								<option value="" disabled>Month</option>
								<option value="01">01 - Jan</option>
								<option value="02">02 - Feb</option>
								<option value="03">03 - Mar</option>
								<option value="04">04 - Apr</option>
								<option value="05">05 - May</option>
								<option value="06">06 - Jun</option>
								<option value="07">07 - Jul</option>
								<option value="08">08 - Aug</option>
								<option value="09">09 - Sept</option>
								<option value="10">10 - Oct</option>
								<option value="11">11 - Nov</option>
								<option value="12">12 - Dec</option>
							</select>
						</div>

						<div class="col-xs-6">
							<select v-model="exp_year" class="form-control">
								<option value="" disabled>Year</option>
								<option v-for="year in next_ten_years" :value="year" :key="year">{{ year }}</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			<div class="row p-b-10">
				<div class="col-xs-12">
					<input type="checkbox" id="mwl_accept_terms" v-model="accept_terms" v-show="false" />
					<label for="mwl_accept_terms">
						<div class="m-r-5 inline" style="width:14px">
							<i class="fa fa-check-square-o" v-show="accept_terms"></i>
							<i class="fa fa-square-o" v-show="!accept_terms"></i>
						</div>
						I ACCEPT THE
						<a href="/terms/credit-cards" target="terms">
							TERM/SERVICE AGREEMENT
						</a>
					</label>
				</div>
			</div>

			<div class="row p-b-10" v-if="can_save_card">
				<div class="col-xs-12">
					<input type="checkbox" id="hps_save_card" v-model="save_card" v-show="false" />
					<label for="hps_save_card">
						<div class="m-r-5 inline" style="width:14px">
							<i class="fa fa-check-square-o" v-show="save_card"></i>
							<i class="fa fa-square-o" v-show="!save_card"></i>
						</div>
						SAVE THIS CARD FOR FUTURE USE
					</label>
				</div>
			</div>

			<input type="submit" v-show="false" ref="submit_button" />

		</form>
	</div>
</template>

<style lang="scss">
@import '../../sass/_global_variables.scss';

.paytrace-payment {
	label {
		font-weight: normal;
		text-transform: uppercase;
	}

	select.form-control option[value=""] {
		display: none;
	}

	input[type="number"]::-webkit-inner-spin-button,
	input[type="number"]::-webkit-outer-spin-button {
		-webkit-appearance: none !important;
		margin: 0 !important;
		-moz-appearance:textfield !important;
	}
}
</style>

<script>

	// Credit Card Patterns
	const visaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/;
	const mastercardRegex = /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/;
	const amExpRegex = /^3[47][0-9]{13}$/;
	const dinersClubRegex = /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/;
	const discoverRegex = /^6(?:011|5[0-9]{2})[0-9]{12}$/;
	const jcbRegex = /^(?:2131|1800|35\d{3})\d{11}$/;

	export default {
		name: "paytrace-payment",

		props: {
			can_save_card: {
				default: false
			},
			public_key_url: {
				type: String,
				required: true
			}
		},

		data: function() {
			return {
				card_number: '',
				exp_month: '',
				exp_year: '',
				cvv: '',
				postal_cd: '',
				save_card: false,
				accept_terms: false
			}
		},

		computed : {
			expiration() {
				return this.exp_month + "/" + this.exp_year;
			},

			next_ten_years() {
				const date = new Date;
				let year = date.getFullYear();
				let years = [];
				for (let i=0;i<10;i++) {
					years.push(year++);
				}
				return years;
			},

			// Determine Card Type based off input
			card_type() {
				if (visaRegex.test(this.card_number)) {
					return "Visa";
				} else if (mastercardRegex.test(this.card_number)) {
					return "Mastercard";
				} else if (amExpRegex.test(this.card_number)) {
					return "AmericanExpress";
				} else if (discoverRegex.test(this.card_number)) {
					return "Discover";
				} else if (dinersClubRegex.test(this.card_number)) {
					return "DinersClub";
				} else if (jcbRegex.test(this.card_number)) {
					return "JCB";
				}

				return "Unknown";
			}
		},

		methods : {
			initializePayTrace () {
				// Set Pay Trace Public Key
				paytrace.setKeyAjax(this.public_key_url);
			},

			// Submission is possible even though the submit button is hidden, this is possible by hitting the enter button or through accessibility extensions.
			// In this situation, we need to redirect the submission to tell our parent we want to process this form.
			onSubmit (event) {
				event.preventDefault();
				this.$emit('submit');   // say we want to submit
			},

			// Return true if the user has populated valid data into the form
			isValid() {
				let errors = [];
				if (!this.card_number || !this.exp_year || !this.exp_month || !this.cvv || !this.postal_cd) {
					errors.push("Please fill out all information before continuing.");
				}
				if (this.cvv.length < 3 || this.cvv.length > 4) {
					errors.push("The security code should be 3 digits unless it is a Master Card which uses 4 digits.");
				}
				if (!this.accept_terms) {
					errors.push("Please accept the Term/Service Agreement before continuing.");
				}
				if (this.card_type === "Unknown") {
					errors.push("Please enter a valid credit card number");
				}

				// If we have errors, push them as a list of errors and return false
				if (errors.length > 0) {
					this.$emit('error', errors);
					return false;
				}

				// else, everything is good
				this.$emit('error', null);    // empty error stack
				return true;
			},

			// Return Encrypted Card Information
			getEncryptedCardInfo () {
				return {
					encrypted_number : paytrace.encryptValue(this.card_number),
					cvv: this.cvv,
					exp_month: this.exp_month,
					exp_year: this.exp_year,
					postal_cd: this.postal_cd,
					save_card: this.save_card,
					card_type: this.card_type
				};
			}
		},

		mounted() {
			// At runtime, include the pay trace script
			let hps = document.createElement('script');
			hps.type = 'text/javascript';
			hps.async = true;
			hps.src = 'https://api.paytrace.com/assets/e2ee/paytrace-e2ee.js';
			let head = document.getElementsByTagName('head')[0];
			head.appendChild(hps);
			hps.onload = this.initializePayTrace;   // we have to wait for the script to load async
		}
	}
</script>



// WEBPACK FOOTER //
// paytrace-payment.vue?124ac755